/**
 * @fileoverview "Gerenciar Agentes" (/fluxo, bloco 3b dos planos): os agentes de
 * IA reutilizáveis. A lista, com o "usado em", e o editor nas abas
 * Conhecimento, Fontes, Inteligência e Testar ficam no mesmo modal (sem modal
 * sobre modal); fechar ou voltar com mudanças não salvas pede confirmação. Ver
 * pede ai_agents.view (o botão da página); criar, editar e excluir,
 * ai_agents.manage (sem ela, o editor só mostra). O bloco "Agente IA" do
 * fluxo abre o modal já no agente dele (initialAgentId) ou num agente novo
 * (null), e fica sabendo do agente criado (onAgentCreated).
 */

import React, { useCallback, useContext, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { Bot, X } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import { AuthContext } from "../../../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../../../hooks/usePermission";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import useStyles from "../TriageManager/styles";
import useAgentStyles from "./styles";
import AgentList from "./AgentList";
import AgentEditor from "./AgentEditor";

const AgentManager = ({ open, onClose, initialAgentId, onAgentCreated }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const canManage = usePermission().can("ai_agents.manage", isAdminUser(user));
  // undefined: lista; null: novo; número: editando esse.
  const [editing, setEditing] = useState(initialAgentId);
  const [confirm, setConfirm] = useState(null);
  const dirtyRef = useRef(false);

  const setDirty = useCallback((dirty) => {
    dirtyRef.current = dirty;
  }, []);

  const leaveEditor = (next) => {
    if (dirtyRef.current) {
      setConfirm(() => next);
      return;
    }
    next();
  };

  const backToList = () => {
    dirtyRef.current = false;
    setEditing(undefined);
  };

  const close = () => {
    dirtyRef.current = false;
    setEditing(undefined);
    onClose();
  };

  const handleClose = () => (editing !== undefined ? leaveEditor(close) : close());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} fullScreen={fullScreen} classes={{ paper: `${classes.paper} ${agentClasses.paperWide}` }}>
      {editing !== undefined ? (
        <AgentEditor
          agentId={editing}
          canManage={canManage}
          onBack={() => leaveEditor(backToList)}
          onClose={handleClose}
          onDirtyChange={setDirty}
          onCreated={(agentId) => {
            setEditing(agentId);
            if (onAgentCreated) onAgentCreated(agentId);
          }}
        />
      ) : (
        <>
          <div className={classes.header}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className={classes.title}>
                <Bot size={20} strokeWidth={1.75} />
                {i18n.t("agentManager.title")}
              </div>
              <div className={agentClasses.subtitle}>{i18n.t("agentManager.subtitle")}</div>
            </div>
            <button type="button" className={classes.iconBtn} onClick={handleClose} aria-label={i18n.t("agentManager.close")}>
              <X />
            </button>
          </div>
          <div className={classes.body}>
            <AgentList canManage={canManage} onEdit={setEditing} onNew={() => setEditing(null)} />
          </div>
        </>
      )}

      <ConfirmationModal
        title={i18n.t("agentManager.unsaved")}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const next = confirm;
          setConfirm(null);
          if (next) next();
        }}
      >
        {i18n.t("agentManager.discardConfirm")}
      </ConfirmationModal>
    </Dialog>
  );
};

export default AgentManager;
